<template>
  <div id="listWidget" class="mb-5">
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <p class="mb-0" style="font-size: 0.85rem; line-height: 1.6; color: #495057;">
          커튼 <code style="font-size: 100%">열기</code> / 물리버튼 OFF·자주열림 과 함께 켜지고,
          <code style="font-size: 100%">닫기</code> / 물리버튼 ON·자주닫힘 과 함께 꺼질 조명을 선택하세요.
          밝기·색온도는 각 조명의 기존 설정을 그대로 사용합니다.
          (포함 미리보기로 잠깐 켜진 조명은 화면을 나가면 원래 상태로 돌아갑니다.)
        </p>
      </div>
    </div>

    <div class="name-edit-card card-item mb-3" v-if="curtainDevice">
      <div class="name-edit-body d-flex align-items-center justify-content-between">
        <span style="font-size: 0.9rem; font-weight: 600;">{{ curtainDevice.name || '커튼' }}</span>
        <button class="btn btn-primary" @click="onSaveClick">저장</button>
      </div>
    </div>

    <div class="section-card card-item mb-3" v-if="availableLights.length > 0">
      <div class="section-header">
        <i class="fa-regular fa-lightbulb me-2 text-primary"></i>
        <span class="section-title">조명 선택 ( {{ group?.name }} )</span>
      </div>
      <div class="section-body">
        <div class="row">
          <LightMappingDeviceItem
            v-for="item in availableLights"
            :key="item._id"
            :group="group"
            :device="item"
            :selectedDevices="selectedLights"
            @deviceChecked="handleDeviceChecked"
          />
        </div>
      </div>
    </div>

    <div
      v-for="og in otherRoomGroups"
      :key="og._id"
      class="section-card card-item mb-3"
    >
      <div
        class="section-header section-header-toggle"
        :class="{ 'section-header-collapsed': !isOtherRoomExpanded(og._id) }"
        role="button"
        @click="toggleOtherRoom(og._id)"
      >
        <i class="fa-brands fa-microsoft me-2 text-primary"></i>
        <span class="section-title flex-fill">{{ og.name }}</span>
        <span
          v-if="selectedCountInGroup(og) > 0"
          class="other-room-badge me-2"
        >포함 {{ selectedCountInGroup(og) }}</span>
        <i
          :class="`fa-solid ${isOtherRoomExpanded(og._id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`"
          style="font-size: 0.85rem;"
        ></i>
      </div>
      <div v-if="isOtherRoomExpanded(og._id)" class="section-body">
        <div class="row">
          <LightMappingDeviceItem
            v-for="item in physicalLampsOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            :selectedDevices="selectedLights"
            @deviceChecked="handleDeviceChecked"
          />
        </div>
      </div>
    </div>

    <div v-if="allSelectableLights.length === 0" class="text-muted px-3" style="font-size: 0.85rem;">
      선택할 수 있는 조명이 없습니다.
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  getCurrentInstance,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
} from 'vue';
import Axios, { VirtualDeviceApi } from '@/api';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useStore } from 'vuex';
import LightMappingDeviceItem from './components/LightMappingDeviceItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';
import { CURTAIN_LINKED_LIGHTS_TYPE } from '@/common/utils/curtainLinkedLights';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();

const selectedLights = ref([]);
const expandedOtherRoomIds = ref([]);
/** 진입 시점 조명 상태 — 포함 미리보기 후 복원용 */
const srcDevices = ref([]);
let restoredOnLeave = false;

const zones = computed(() => store.getters['home/getZones']);

const groups = computed(() => {
  const list = [];
  zones.value?.forEach((zone) => list.push(...(zone.groups || [])));
  return list;
});

const group = computed(() => groups.value?.find((g) => String(g._id) === String(route.params.groupId)));

const curtainDevice = computed(() => {
  return (group.value?.devices || []).find((d) => String(d._id) === String(route.params.deviceId));
});

const physicalLampsOf = (g) => {
  return (g?.devices || []).filter((d) => d.type === 1 && !d.isVirtual);
};

const currentZone = computed(() => {
  const gid = String(route.params.groupId);
  return zones.value?.find((z) => (z.groups || []).some((g) => String(g._id) === gid));
});

const availableLights = computed(() => physicalLampsOf(group.value));

const otherRoomGroups = computed(() => {
  const gid = String(route.params.groupId);
  return (currentZone.value?.groups || []).filter(
    (g) => String(g._id) !== gid && physicalLampsOf(g).length > 0,
  );
});

const allSelectableLights = computed(() => {
  const list = [...availableLights.value];
  for (const og of otherRoomGroups.value) {
    list.push(...physicalLampsOf(og));
  }
  return list;
});

const findGroupIdForDevice = (deviceId) => {
  const sid = String(deviceId);
  if (availableLights.value.some((d) => String(d._id) === sid)) {
    return group.value?._id;
  }
  for (const og of otherRoomGroups.value) {
    if (physicalLampsOf(og).some((d) => String(d._id) === sid)) {
      return og._id;
    }
  }
  return group.value?._id;
};

const isOtherRoomExpanded = (groupId) => expandedOtherRoomIds.value.includes(String(groupId));

const toggleOtherRoom = (groupId) => {
  const sid = String(groupId);
  if (expandedOtherRoomIds.value.includes(sid)) {
    expandedOtherRoomIds.value = expandedOtherRoomIds.value.filter((id) => id !== sid);
  } else {
    expandedOtherRoomIds.value = [...expandedOtherRoomIds.value, sid];
  }
};

const selectedCountInGroup = (og) => {
  const ids = new Set(physicalLampsOf(og).map((d) => String(d._id)));
  return selectedLights.value.filter((d) => ids.has(String(d._id))).length;
};

const expandRoomsForSelected = () => {
  const currentGid = String(route.params.groupId);
  const ids = new Set();
  for (const sel of selectedLights.value) {
    const gid = findGroupIdForDevice(sel._id);
    if (gid != null && String(gid) !== currentGid) {
      ids.add(String(gid));
    }
  }
  expandedOtherRoomIds.value = [...ids];
};

const toScalar = (raw, fallback = 0) => {
  if (Array.isArray(raw)) return toScalar(raw[0], fallback);
  if (raw === undefined || raw === null || raw === '') return fallback;
  const n = Number(raw);
  return Number.isNaN(n) ? fallback : n;
};

const resolveLampItemState = (device) => {
  const item = device?.item ?? {};
  if (device?.onVirtual === true) {
    const st = item.state ?? {};
    return {
      onoff: toScalar(st.onoff ?? 0, 0),
      bright: toScalar(st.bright ?? item.bright, 50),
      color: toScalar(st.color ?? item.color, 0),
    };
  }
  return {
    onoff: toScalar(item.onoff, 0),
    bright: toScalar(item.bright, 50),
    color: toScalar(item.color, 0),
  };
};

const publishStatus = async (devices) => {
  const lampItems = (devices || []).filter((d) => d.type === 1);
  const byController = {};
  for (const it of lampItems) {
    const cid = it.controllerId;
    if (!byController[cid]) byController[cid] = [];
    byController[cid].push(it);
  }
  for (const controllerId of Object.keys(byController)) {
    const ctrlGroup = byController[controllerId];
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    const deviceIds = [];
    for (const it of ctrlGroup) {
      deviceIds.push(it._id);
      const ports = Array.isArray(it.item?.ports)
        ? it.item.ports
        : (it.item?.ports != null ? [it.item.ports] : []);
      const lampState = resolveLampItemState(it);
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(lampState.onoff);
        brightArr.push(lampState.bright);
        colorArr.push(lampState.color);
      }
    }
    if (portsArr.length > 0) {
      mqttControl.onPublishBatch(controllerId, deviceIds, {
        serial: controllerId,
        cmd: 'cs',
        type: 1,
        data: { ports: portsArr, onoff: onoffArr, bright: brightArr, color: colorArr },
      });
    }
  }
};

const restorePreviewLights = async () => {
  if (restoredOnLeave || srcDevices.value.length === 0) return;
  restoredOnLeave = true;
  try {
    await publishStatus(srcDevices.value);
  } catch (e) {
    console.error('커튼 연동 미리보기 복원 실패:', e);
  }
};

const normalizeVdItems = (items) => {
  if (items == null) return [];
  if (Array.isArray(items)) return items;
  if (typeof items === 'object') return Object.values(items);
  return [];
};

const memberIdOf = (it) => {
  if (it == null) return null;
  if (typeof it === 'number' || typeof it === 'string') return it;
  return it._id ?? it.id ?? null;
};

const mergeSelectedFromItems = (items) => {
  const ids = new Set();
  for (const it of items || []) {
    const id = memberIdOf(it);
    if (id == null) continue;
    // type 없거나 1이면 조명으로 간주 (저장 포맷 호환)
    const t = it?.type != null ? Number(it.type) : 1;
    if (t === 1) ids.add(String(id));
  }
  return allSelectableLights.value.filter((d) => ids.has(String(d._id)));
};

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', {
    title: '커튼 조명 연동',
    isShowMenu: false,
    isShowZone: false,
    isShowProfile: false,
    isShowRightHome: true,
  });
});

onBeforeUnmount(() => {
  restorePreviewLights();
  emitter.emit('onChangeHeaderSetting', {
    title: '',
    isShowMenu: true,
    isShowZone: true,
    isShowProfile: true,
    isShowRightHome: false,
  });
});

onBeforeRouteLeave(async (to, from, next) => {
  await restorePreviewLights();
  next();
});

onMounted(async () => {
  await store.dispatch('home/fetchAllZones');
  if (!curtainDevice.value) {
    emitter.emit('showPopAlert', {
      description: '커튼 장치를 찾을 수 없습니다.',
      okCallback: () => router.go(-1),
    });
    return;
  }
  srcDevices.value = JSON.parse(JSON.stringify(allSelectableLights.value));
  await loadExisting();
});

const loadExisting = async () => {
  try {
    const result = await Axios.get(
      VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', route.params.deviceId),
    );
    if (result.resultCode === '9001') {
      selectedLights.value = [];
      return;
    }
    if (result.resultCode !== '0000') {
      throw new Error(result.resultMessage);
    }
    const mt = Number(result.resultData?.mappingType);
    const list = normalizeVdItems(result.resultData?.items);
    const lights = mergeSelectedFromItems(list);
    // mappingType=2 우선. 구버전 API가 1로 저장한 커튼 VD도 멤버가 있으면 복원
    if (mt === CURTAIN_LINKED_LIGHTS_TYPE || lights.length > 0) {
      selectedLights.value = lights;
      expandRoomsForSelected();
      return;
    }
    selectedLights.value = [];
  } catch (e) {
    emitter.emit('showPopAlert', {
      description: '연동 조명 정보 조회에 실패했습니다.',
      okCallback: () => router.go(-1),
    });
  }
};

const handleDeviceChecked = (data) => {
  const liveDevice = allSelectableLights.value.find((d) => String(d._id) === String(data.device._id)) || data.device;
  const id = String(liveDevice._id);
  if (data.isChecked) {
    if (!selectedLights.value.some((d) => String(d._id) === id)) {
      selectedLights.value = [...selectedLights.value, liveDevice];
    }
  } else {
    selectedLights.value = selectedLights.value.filter((d) => String(d._id) !== id);
  }
};

/** DB 저장용 최소 필드 (Vue 반응형/불필요 필드 제거) */
const resolveSelectedItems = () => {
  return selectedLights.value.map((sel) => {
    const current = allSelectableLights.value.find((d) => String(d._id) === String(sel._id)) || sel;
    const st = resolveLampItemState(current);
    return {
      _id: current._id,
      type: 1,
      controllerId: current.controllerId,
      name: current.name,
      item: {
        ports: current.item?.ports,
        bright: st.bright,
        color: st.color,
        onoff: st.onoff,
      },
    };
  });
};

const onSaveClick = async () => {
  const payload = {
    items: resolveSelectedItems(),
    mappingType: CURTAIN_LINKED_LIGHTS_TYPE,
  };
  try {
    const result = await Axios.patch(
      VirtualDeviceApi.UPDATE_VIRTUAL_DEVICE.replace(':id', route.params.deviceId),
      payload,
    );
    if (result.resultCode === '0000') {
      const savedMt = Number(result.resultData?.mappingType);
      const savedItems = normalizeVdItems(result.resultData?.items);
      if (payload.items.length > 0 && savedItems.length === 0) {
        emitter.emit('showPopAlert', {
          description: '연동 조명 저장에 실패했습니다. api-server 배포 후 다시 시도해주세요.',
        });
        return;
      }
      if (payload.items.length > 0 && savedMt !== CURTAIN_LINKED_LIGHTS_TYPE && savedMt !== 1) {
        console.warn('curtain linked lights mappingType unexpected:', savedMt);
      }
      await restorePreviewLights();
      const cleared = (payload.items || []).length === 0;
      emitter.emit('showPopAlert', {
        description: cleared
          ? '커튼 조명 연동이 해제되었습니다.'
          : '커튼 조명 연동이 저장되었습니다.',
        okCallback: () => router.go(-1),
      });
      return;
    }
    emitter.emit('showPopAlert', { description: result.resultMessage });
  } catch (e) {
    emitter.emit('showPopAlert', { description: '커튼 조명 연동 저장 중 오류가 발생하였습니다.' });
  }
};
</script>

<style scoped>
.instruction-card,
.name-edit-card,
.section-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.instruction-header,
.section-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.section-header-toggle {
  cursor: pointer;
  user-select: none;
}

.section-header-collapsed {
  border-bottom: none;
}

.instruction-title,
.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.other-room-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d6efd;
  background-color: #e7f1ff;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  white-space: nowrap;
}

.instruction-body,
.section-body {
  padding: 0.75rem 1rem;
}

.name-edit-body {
  padding: 0.75rem 1rem;
}
</style>
